import {
  useInfiniteQuery,
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'

import type { WorkflowConfig } from '@/schema'
import { TABLE_PAGE_SIZE } from '@/utils/constants'

import {
  COMPANY_SORT_BY_NAME,
  WORKFLOW_CONFIG_QUERY_KEYS,
  WORKFLOW_QUERY_CONFIG,
} from './constants'
import { workflowConfigApi } from './services'
import type {
  AddCompaniesRequest,
  AddCompaniesResponse,
  DeleteWorkflowDefinitionResponse,
  UpdateWorkflowDefinitionRequest,
  UpdateWorkflowDefinitionResponse,
  WorkflowSource,
} from './types'

export function useListCompanies(
  source: WorkflowSource,
  searchQuery?: string,
  enabled: boolean = true
) {
  return useInfiniteQuery({
    queryKey: WORKFLOW_CONFIG_QUERY_KEYS.companiesListFiltered(
      source,
      searchQuery
    ),
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const response = await workflowConfigApi.listCompanies({
        limit: TABLE_PAGE_SIZE,
        offset: pageParam * TABLE_PAGE_SIZE,
        source,
        sort_by: COMPANY_SORT_BY_NAME,
        ...(searchQuery && { q: searchQuery }),
      })
      return {
        data: response.data,
        pagination: {
          has_next: response.pagination.has_next,
          total_items: response.pagination.total_items,
        },
      }
    },
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.pagination.has_next ? allPages.length : undefined
    },
    initialPageParam: WORKFLOW_QUERY_CONFIG.INITIAL_PAGE_PARAM,
    staleTime: WORKFLOW_QUERY_CONFIG.STALE_TIME,
    enabled,
  })
}

export function useWorkflowDefinitionByCompany(
  companyId: string | null
): UseQueryResult<WorkflowConfig | null, Error> {
  return useQuery({
    queryKey: WORKFLOW_CONFIG_QUERY_KEYS.workflowByCompany(companyId || ''),
    queryFn: async () => {
      if (!companyId) return null
      const response =
        await workflowConfigApi.getWorkflowDefinitionByCompany(companyId)
      // API returns data as array with single item
      return Array.isArray(response.data) && response.data.length > 0
        ? response.data[0]
        : null
    },
    enabled: !!companyId,
    staleTime: WORKFLOW_QUERY_CONFIG.STALE_TIME,
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
    refetchOnMount: 'always',
  })
}

export function useAddCompaniesToWorkflow(): UseMutationResult<
  AddCompaniesResponse,
  Error,
  AddCompaniesRequest
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: AddCompaniesRequest) =>
      workflowConfigApi.addCompaniesToWorkflow(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: WORKFLOW_CONFIG_QUERY_KEYS.all,
      })
    },
  })
}

export function useUpdateWorkflowDefinition(): UseMutationResult<
  UpdateWorkflowDefinitionResponse,
  Error,
  {
    definitionId: string
    companyId: string
    data: UpdateWorkflowDefinitionRequest
  }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ definitionId, data }) =>
      workflowConfigApi.updateWorkflowDefinition(definitionId, data),
    onMutate: async ({ definitionId, data }) => {
      await queryClient.cancelQueries({
        queryKey: WORKFLOW_CONFIG_QUERY_KEYS.workflowByCompany(definitionId),
      })

      const previousData = queryClient.getQueryData<WorkflowConfig | null>(
        WORKFLOW_CONFIG_QUERY_KEYS.workflowByCompany(definitionId)
      )

      if (previousData && data.reminder_configurations) {
        queryClient.setQueryData<WorkflowConfig | null>(
          WORKFLOW_CONFIG_QUERY_KEYS.workflowByCompany(definitionId),
          {
            ...previousData,
            reminder_configurations: data.reminder_configurations,
          }
        )
      }

      return { previousData, definitionId }
    },
    onError: (_err, _variables, context) => {
      if (context?.previousData && context?.definitionId) {
        queryClient.setQueryData(
          WORKFLOW_CONFIG_QUERY_KEYS.workflowByCompany(context.definitionId),
          context.previousData
        )
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: WORKFLOW_CONFIG_QUERY_KEYS.workflowByCompany(
          variables.definitionId
        ),
      })
    },
  })
}

export function useDeleteWorkflowDefinition(): UseMutationResult<
  DeleteWorkflowDefinitionResponse,
  Error,
  { definitionId: string; companyId: string }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ definitionId }) =>
      workflowConfigApi.deleteWorkflowDefinition(definitionId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: WORKFLOW_CONFIG_QUERY_KEYS.workflowByCompany(
          variables.companyId
        ),
      })
    },
  })
}
